import type { ModalProps } from "../ui";
import { Button, Flex, Modal, theme } from "../ui";
import React from "react";
import { handleDialogEnterKeyDown } from "./dialogKeyboard";
import { useAutoFocusInContainer } from "./hooks/useAutoFocusInContainer";
import { useSubmitLock } from "./hooks/useSubmitLock";
import { spacing } from "../settings/settingsListDesign";

export const FORM_MODAL_WIDTH = "min(960px, 96vw)";

export type ThemedModalProps = ModalProps & {
  /** Native form action in the title row; footer actions stay the default. */
  submitInHeader?: boolean;
  headerExtra?: React.ReactNode;
  hideSubmit?: boolean;
  /**
   * Wenn gesetzt: OK-Button wird zu htmlType="submit" form={formId}. Enter im Form-Body
   * triggert dann nativ den OK-Klick. Die innere Form muss id={formId} + onFinish setzen.
   */
  formId?: string;
  /** Spiegelt confirmLoading semantisch — kommuniziert "Submit läuft". */
  submitting?: boolean;
  /** Default true (außer bei okType="danger"): erstes interaktives Feld bekommt Fokus. */
  autoFocusFirstField?: boolean;
  /** Default true: Enter im Body triggert OK über den nativen Form-Submit. */
  submitOnEnter?: boolean;
};

/**
 * Modal mit Theme-Tokens + standardisierter Tastatur-UX:
 *  - Escape schließt (Antd-Default, nicht überschrieben).
 *  - Enter im Form-Body submittet, wenn formId gesetzt.
 *  - AutoFocus auf erstes Eingabefeld nach Open.
 *  - Mask-Klick schließt NICHT, wenn eine Form drin ist (Eingabe-Schutz) — überschreibbar.
 *
 * Produktneutraler Port aus m1member; nutzt nur antd + den Shared-AutoFocus-Hook.
 */
export function ThemedModal({
  styles,
  formId,
  submitting,
  autoFocusFirstField,
  submitOnEnter = true,
  okButtonProps,
  confirmLoading,
  modalRender,
  open,
  okType,
  maskClosable,
  submitInHeader = false,
  headerExtra,
  hideSubmit = false,
  title,
  okText,
  onOk,
  ...props
}: ThemedModalProps) {
  const { token } = theme.useToken();
  const elevatedBg = { background: token.colorBgElevated };
  const mergeElevatedStyles = React.useCallback(
    (baseStyles: Record<string, React.CSSProperties> | undefined): ModalProps["styles"] =>
      ({
        ...baseStyles,
        container: { ...baseStyles?.container, ...elevatedBg },
        header: { ...baseStyles?.header, ...elevatedBg },
        body: { ...baseStyles?.body, ...elevatedBg },
      }) as ModalProps["styles"],
    [elevatedBg]
  );

  const isDanger = okType === "danger";
  const autoFocusEnabled = autoFocusFirstField ?? !isDanger;
  const containerRef = useAutoFocusInContainer({ open: !!open, enabled: autoFocusEnabled });

  const submitButtonProps =
    formId && submitOnEnter
      ? { ...okButtonProps, htmlType: "submit" as const, form: formId }
      : okButtonProps;

  const effectiveConfirmLoading = submitting ?? confirmLoading;
  const { submitBlocked, armSubmitLock } = useSubmitLock({
    open: !!open && submitInHeader,
    loading: !!effectiveConfirmLoading,
    formId: submitInHeader && submitOnEnter ? formId : undefined,
    submitVisible: submitInHeader && !hideSubmit,
    submitDisabled: okButtonProps?.disabled,
  });
  const header = submitInHeader ? <Flex justify="space-between" align="center" gap={spacing.md} wrap="wrap">
    <span>{title}</span>
    <Flex align="center" gap={spacing.sm} wrap="wrap">
      {headerExtra}
      {!hideSubmit && <Button {...submitButtonProps} type="primary" loading={submitBlocked}
        disabled={okButtonProps?.disabled || submitBlocked}
        onClick={formId && submitOnEnter ? undefined : event => { if (armSubmitLock()) return onOk?.(event as React.MouseEvent<HTMLButtonElement>); }}>
        {okText}
      </Button>}
    </Flex>
  </Flex> : title;

  // Wenn Form drin (formId), schließt ein Mask-Klick NICHT — sonst gehen Eingaben verloren.
  // Aufrufer kann maskClosable explizit überschreiben.
  const resolvedMaskClosable = maskClosable ?? !formId;

  const renderWithRef = React.useCallback(
    (node: React.ReactNode) => {
      const wrapped = (
        <div ref={containerRef} onKeyDown={handleDialogEnterKeyDown} style={{ display: "contents" }}>
          {node}
        </div>
      );
      return modalRender ? modalRender(wrapped) : wrapped;
    },
    [containerRef, modalRender]
  );

  const resolvedStyles = React.useMemo<ModalProps["styles"]>(() => {
    if (typeof styles === "function") {
      return ((info: Parameters<typeof styles>[0]) =>
        mergeElevatedStyles(styles(info) as Record<string, React.CSSProperties> | undefined)) as ModalProps["styles"];
    }
    return mergeElevatedStyles(styles as Record<string, React.CSSProperties> | undefined);
  }, [mergeElevatedStyles, styles]);

  return (
    <Modal
      {...props}
      title={header}
      okText={okText}
      onOk={onOk}
      footer={submitInHeader ? null : props.footer}
      open={open}
      okType={okType}
      okButtonProps={submitButtonProps}
      confirmLoading={effectiveConfirmLoading}
      modalRender={renderWithRef}
      maskClosable={resolvedMaskClosable}
      styles={resolvedStyles}
    />
  );
}
